import { NavigationButton, NavigationLink } from '@hub/navigation';
import React from 'react';
import { Text } from '@hub/text';
import { H4, H5 } from '@hub/heading';
import { Input, InputGroup, InputRightElement } from '@hub/input';
import { ClearInvertedSmallIcon, LocationDropPinMediumIcon } from '@hub/icon';
import { Box } from '@hub/box';
import { Stack } from '@hub/stack';
import { useNearestCentres } from '../../../hooks/use-nearest-centres';
import { IconButton } from '@hub/button';
import { isDefinedAndNotNull } from '../../../lib';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const useCentresByState = (items, keyword) => React.useMemo(() => Object.entries(items.reduce((byState, item) => {
  const {
    state
  } = item.centre;
  if (!byState[state]) {
    byState[state] = [];
  }
  byState[state].push(item);
  return byState;
}, {})).filter(_ref => {
  let [, items] = _ref;
  return keyword === '' || items.some(_ref2 => {
    let {
      centre
    } = _ref2;
    return centre.title.toLowerCase().includes(keyword.toLowerCase());
  });
}).map(_ref3 => {
  let [state, items] = _ref3;
  return [state, items.filter(_ref4 => {
    let {
      centre
    } = _ref4;
    return centre.title.toLowerCase().includes(keyword.toLowerCase());
  }).sort((a, b) => a.centre.title > b.centre.title ? 1 : -1)];
}).sort((_ref5, _ref6) => {
  let [a] = _ref5;
  let [b] = _ref6;
  return a > b ? 1 : -1;
}), [items, keyword]);
const CentreList = _ref7 => {
  let {
    children,
    onClickLink
  } = _ref7;
  return /*#__PURE__*/_jsx(Stack, {
    shouldWrapChildren: false,
    "data-id": "header-menus_find-centre-menu_CentreList_Stack",
    children: children.map((item, key) => /*#__PURE__*/_jsx(NavigationLink, {
      href: item.url,
      size: "md",
      icon: /*#__PURE__*/_jsx(LocationDropPinMediumIcon, {
        boxSize: 'size-icon-sm',
        "data-id": "header-menus_find-centre-menu_CentreList_LocationDropPinMediumIcon"
      }),
      onClick: event => onClickLink === null || onClickLink === void 0 ? void 0 : onClickLink(event, [item]),
      "data-id": "header-menus_find-centre-menu_CentreList_NavigationLink",
      children: item.label
    }, key))
  });
};
export const FindCentreMenu = _ref8 => {
  let {
    country,
    items,
    onClickLink
  } = _ref8;
  const centres = React.useMemo(() => items.map(_ref9 => {
    let {
      centre
    } = _ref9;
    return centre;
  }), [items]);
  const [keyword, setKeyword] = React.useState('');
  const centresByState = useCentresByState(items, keyword);
  const [nearestCentres, getSuggestedCentresNearMe] = useNearestCentres(centres);
  const suggestedCentres = React.useMemo(() => nearestCentres.suggestedCentres.map(suggestedCentre => items.find(_ref10 => {
    let {
      centre
    } = _ref10;
    return centre.slug === suggestedCentre.slug;
  })).filter(isDefinedAndNotNull), [items, nearestCentres.suggestedCentres]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(H4, {
      as: Text,
      "data-id": "header-menus_find-centre-menu_CentreList_H4",
      children: "Find a centre"
    }), /*#__PURE__*/_jsxs(InputGroup, {
      backgroundColor: "surfaceTertiary",
      borderStyle: "solid",
      borderWidth: 1,
      borderColor: "borderSecondary",
      color: "textSecondary",
      "data-id": "header-menus_find-centre-menu_CentreList_InputGroup",
      children: [/*#__PURE__*/_jsx(Input, {
        value: keyword,
        onChange: event => setKeyword(event.target.value),
        placeholder: "Search by centre name",
        borderColor: "borderSecondary",
        "data-id": "header-menus_find-centre-menu_CentreList_InputGroup_Input"
      }), /*#__PURE__*/_jsx(InputRightElement, {
        "data-id": "header-menus_find-centre-menu_CentreList_InputGroup_InputRightElement",
        children: /*#__PURE__*/_jsx(IconButton, {
          variant: "transparent",
          onClick: () => setKeyword(''),
          "aria-label": "Clear search text",
          boxSize: "size-icon-sm",
          colorScheme: 'licorice',
          icon: /*#__PURE__*/_jsx(ClearInvertedSmallIcon, {
            sx: {
              color: 'licorice',
              visibility: keyword === '' ? 'hidden' : 'visible'
            },
            "data-id": "header-menus_find-centre-menu_CentreList_ClearInvertedSmallIcon"
          }),
          "data-id": "header-menus_find-centre-menu_CentreList_InputRightElement_IconButton"
        })
      })]
    }), /*#__PURE__*/_jsx(NavigationButton, {
      size: "md",
      isLoading: nearestCentres.loadingLocation,
      onClick: getSuggestedCentresNearMe,
      "data-id": "header-menus_find-centre-menu_CentreList_NavigationButton",
      children: "Or use your current location"
    }), /*#__PURE__*/_jsxs(Stack, {
      direction: "column",
      shouldWrapChildren: false,
      sx: {
        gap: 'spacing-md'
      },
      "data-id": "header-menus_find-centre-menu_CentreList_Stack",
      children: [(suggestedCentres === null || suggestedCentres === void 0 ? void 0 : suggestedCentres.length) > 0 && /*#__PURE__*/_jsxs(Box, {
        "data-id": "header-menus_find-centre-menu_CentreList_Box",
        children: [/*#__PURE__*/_jsx(H5, {
          as: Text,
          "data-id": "header-menus_find-centre-menu_CentreList_Box_H5",
          children: "Suggested"
        }), /*#__PURE__*/_jsx(CentreList, {
          onClickLink: onClickLink,
          "data-id": "header-menus_find-centre-menu_Box_CentreList",
          children: suggestedCentres
        })]
      }), keyword === '' && /*#__PURE__*/_jsx(_Fragment, {
        children: /*#__PURE__*/_jsxs(Box, {
          "data-id": "header-menus_find-centre-menu_CentreList_Box",
          children: [/*#__PURE__*/_jsx(H5, {
            as: Text,
            "data-id": "header-menus_find-centre-menu_CentreList_Box_H5",
            children: "Country"
          }), /*#__PURE__*/_jsx(NavigationLink, {
            size: "md",
            href: "/",
            "data-id": "header-menus_find-centre-menu_CentreList_Box_NavigationLink",
            children: country
          })]
        })
      }), centresByState.map(_ref11 => {
        let [state, centres] = _ref11;
        return /*#__PURE__*/_jsxs(Box, {
          "data-id": "header-menus_find-centre-menu_CentreList_Box",
          children: [/*#__PURE__*/_jsx(H5, {
            as: Text,
            "data-id": "header-menus_find-centre-menu_CentreList_Box_H5",
            children: state
          }), /*#__PURE__*/_jsx(CentreList, {
            onClickLink: onClickLink,
            "data-id": "header-menus_find-centre-menu_Box_CentreList",
            children: centres
          })]
        }, state);
      }), centresByState.length === 0 && /*#__PURE__*/_jsx(Text, {
        "data-id": "header-menus_find-centre-menu_CentreList_Text",
        children: "No centres matched"
      })]
    })]
  });
};